/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, Button, Dropdown, Space } from "antd";
import type { MenuProps } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { changeAppState, getAppState, getLng, getDict, getUser, onChangeTheme, onChangeCompact, onChangeLng, onChangeShowDesktopIconsTitle, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tbtype = {
  shape:"default" | "circle" | "round",
  size:"small" | "middle" | "large",
  danger:boolean,
  ghost:boolean,
  block:boolean,
  menu?:boolean,
  menuPlacement?:"bottom" | "bottomLeft" | "bottomRight" | "top" | "topLeft" | "topRight",
  variant?:"solid" | "outlined" | "dashed" | "filled" | "text" | "link" | undefined,
  color?:"default" | "primary" | "danger" | "pink" | "purple" | "cyan" | undefined,
  type?:"default" | "primary" | "dashed" | "link" | "text" | undefined
};

const { rz } = window;

const RZbutton:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZbutton.displayName = "Button";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, content} = attributes;
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath] || {};
  if (!elState.attributes) { elState.attributes = {...attributes} }
  const {icon, iconClass, iconColor, bgColor, iconSize} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState && parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = (elState.userStyle ? {...elState.userStyle} : {});
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    if (bgColor) { est.backgroundColor = bgColor }
    if (!est.minWidth) { est.minWidth = "2.2em" }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions, bgColor]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons key={key + "-icon"} icopath={icon} attrs={{className: iconClass, color: iconColor, bgcolor: bgColor, size: iconSize}} />) }
    return ico;
  }, [key, icon, iconClass, iconColor, bgColor, iconSize]);

  const btype = useMemo<Tbtype>(() => {
    const typ:Tbtype = {type: "default", variant: "outlined", shape: "default", size: "middle", danger: false, ghost: false, block: false};
    (elState.attributes.type || attributes.type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["solid", "outlined", "dashed", "filled", "text", "link"].indexOf(tt)) { typ.variant = tt }
      else if (~["default", "primary", "danger", "pink", "purple", "cyan"].indexOf(tt)) { typ.color = tt }
      // else if (tt === "danger") { typ.danger = true }
      else if (tt === "ghost") { typ.ghost = true }
      else if (~["block", "expand"].indexOf(tt)) { typ.block = true }
      else if (~["circle", "round"].indexOf(tt)) { typ.shape = tt }
      else if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
      else if (~["menu", "dropdown"].indexOf(tt)) { typ.menu = true }
    });

    if (elState.attributes.hasitems || elState.attributes.menu === "true" || elState.attributes.dropdown === "true" || attributes.hasitems) { typ.menu = true }
    if (~["bottom", "bottomLeft", "bottomRight", "top", "topLeft", "topRight"].indexOf(elState.attributes.menuPlacement)) { typ.menu = true; typ.menuPlacement = elState.attributes.menuPlacement }
    if (elState.attributes.color) { typ.color = elState.attributes.color }
    if (elState.attributes.variant) { typ.variant = elState.attributes.variant }
    if (elState.attributes.type) { typ.type = elState.attributes.type }
    if (elState.attributes.shape) { typ.shape = elState.attributes.shape }
    if (elState.attributes.size) { typ.size = elState.attributes.size }
    if (elState.attributes.danger) { typ.danger = (elState.attributes.danger === "true") }
    if (elState.attributes.ghost) { typ.ghost = (elState.attributes.ghost === "true") }
    if (elState.attributes.block) { typ.block = (elState.attributes.block === "true") }

    return typ;
  }, [elState.attributes, attributes]);

  const menuItems = useMemo<MenuProps["items"]>(() => {
    if (!content || !content.length) { return undefined }
    // console.log({hasitems: elState.attributes.hasitems, content});

    const getSubContent = (cnt) => {
      if (!cnt || !rz.isArray(cnt) || !cnt.length) { return undefined }
      return cnt.map((mm) => {
        const perm1 = fnc.checkPermissions(user, {...mm?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[mm.fullPath];
        const key1 = mm.key || rz.strMakeID(16);
        let icon1:React.ReactElement | undefined;
        // eslint-disable-next-line max-len
        if (mm?.attributes?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.attributes.icon} attrs={{className: mm.attributes?.iconClass || "rz-margin-right-5", color: mm.attributes?.iconColor, size: mm.attributes?.iconSize || "1.2em"}} />) }
        if (mm?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.icon} attrs={{className: mm?.iconClass || "rz-margin-right-5", color: mm?.iconColor, size: mm?.iconSize || "1.2em"}} />) }
        const label1 = (
          rz.isString(mm) ? mm : (mm?.attributes ? (
            mm.attributes["title_" + lng] || mm.attributes.title || mm.attributes["title_" + rlng] || mm.attributes["label_" + lng] || mm.attributes.label || mm.attributes["label_" + rlng] || ""
          ) : (
            mm["title_" + lng] || mm.title || mm["title_" + rlng] || mm["label_" + lng] || mm.label || mm["label_" + rlng] || ""
          )
          ));
        return {
          key: key1, icon: icon1,
          label: label1,
          danger: (mm?.attributes?.type && ~mm?.attributes?.type.indexOf("danger") ? true : undefined),
          disabled: (state1?.disabled || perm1.disabled === true),
          style: (state1?.visible === false || perm1.visible === false ? {display: "none"} : undefined),
          children: getSubContent(mm?.content)
        };
      });
    };
    return getSubContent(content);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elState.attributes.hasitems, elState.attributes.content, lng, user, appState]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState?.attributes["title_" + lng] || elState?.attributes.title || elState?.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const tooltip = useMemo<string | undefined>(() => (elState?.attributes["tooltip_" + lng] || elState?.attributes.tooltip || elState?.attributes["tooltip_" + rlng] || attributes["tooltip_" + lng] || attributes.tooltip || attributes["tooltip_" + rlng]), [elState?.attributes, attributes, lng]);

  const onClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (fnc.config?.isDev) console.log({action: "<Button key='" + key + "'> -> onClick()"});
    if (rootNamespace && (attributes.onClick || attributes.onclick) && rootNamespace[attributes.onClick || attributes.onclick]) {
      const clientFNC = rootNamespace[attributes.onClick || attributes.onclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret?.globalState) {
            if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret?.globalState) {
          if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, navigate, safe]);

  const onMenuClick = useCallback((event) => {
    if (fnc.config?.isDev) console.log({action: "<DropdownMenu key='" + key + "'> -> onMenuClick()", menuKey: event.key});
    if (rootNamespace && (attributes.onMenuClick || attributes.onmenuclick) && rootNamespace[attributes.onMenuClick || attributes.onmenuclick]) {
      const clientFNC = rootNamespace[attributes.onMenuClick || attributes.onmenuclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key: event.key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret?.globalState) {
            if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}) }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key: event.key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret?.globalState) {
          if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}) }
      }
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, navigate, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // if (iType.rules) { frmItemProps.name = String(key); frmItemProps.rules = iType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  if (btype.menu && content?.length && menuItems) {
    // console.log(menuItems);
    // DropdownMenu
    return wrapperForm(
      <Dropdown
        key={key + "-dropdown"}
        trigger={["click"]}
        menu={{items: menuItems, onClick: onMenuClick}}
        placement={btype.menuPlacement}
      >
        <Button
          key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip} icon={rico}
          type={btype.type} shape={btype.shape} size={btype.size}
          block={btype.block} // similary as rz-width-expand
          variant={btype.variant} color={btype.color}
          danger={btype.danger} ghost={btype.ghost}
          onClick={onClick}
          disabled={elState?.disabled || permissions.disabled === true}
          hidden={elState?.visible === false || permissions.visible === false}
        >
          {title}
        </Button>
      </Dropdown>
    );
  }

  // @ts-ignore
  if (children?.length) {
    // ButtonGroup
    return wrapperForm(
      <Space.Compact block
        key={key} className={clsu.join(" ")} style={styl} size={btype.size} uk-tooltip={tooltip}
      >
        {children}
      </Space.Compact>
    );
  }

  // Simple Button
  // The recommended order is: type -> shape -> size -> loading -> disabled
  return wrapperForm(
    <Button
      key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip} icon={rico}
      type={btype.type} shape={btype.shape} size={btype.size}
      block={btype.block} // similary as rz-width-expand
      variant={btype.variant} color={btype.color}
      danger={btype.danger} ghost={btype.ghost}
      onClick={onClick}
      disabled={elState?.disabled || permissions.disabled === true}
      hidden={elState?.visible === false || permissions.visible === false}
    >
      {title}
    </Button>
  );
}

export default RZbutton;
/* eslint-enable @typescript-eslint/no-unused-vars */
